import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  AssignmentCustomerResponse,
  baseAuthSelectors,
  baseAssignmentsActions,
  baseAssignmentsSelectors,
  BaseAvatarSize,
  BaseButtonSize,
  BaseCoreState,
  baseRatingValidator,
  BaseTokenInterface,
  CustomerProfileRatingListItemResponse,
} from 'base';
import { AbstractControl, Validators, FormControl, NonNullableFormBuilder } from '@angular/forms';
import { Store } from '@ngrx/store';
import { filter, map, takeUntil } from 'rxjs/operators';
import { combineLatest, Subject } from 'rxjs';

@Component({
  selector: 'base-customer-employee-reviews-tab',
  templateUrl: './base-customer-employee-reviews-tab.component.html',
})
export class BaseCustomerEmployeeReviewsTabComponent implements OnInit, OnDestroy {
  @Input() assignment: AssignmentCustomerResponse | undefined;
  @Input() profileId: number;
  @Input() isOfferConfirmed: boolean;
  reviews: Array<CustomerProfileRatingListItemResponse>;
  ownReview: CustomerProfileRatingListItemResponse;
  submitted = false;
  isOwnReviewEdited = false;
  avatarSize = BaseAvatarSize;
  buttonSizes = BaseButtonSize;
  ownRatingFormGroup = this.fb.group({
    rating: [0, baseRatingValidator()],
    note: ['', [Validators.required]],
  });

  private destroy$ = new Subject();

  constructor(private fb: NonNullableFormBuilder, private store: Store<BaseCoreState>) {}

  ngOnInit(): void {
    if (!this.assignment && !this.profileId) {
      return;
    }

    if (!this.profileId) {
      this.profileId = (this.assignment as AssignmentCustomerResponse).profile.id;
    }
    this.store.dispatch(baseAssignmentsActions.getCustomerReviews({ payload: this.profileId }));

    combineLatest([
      this.store.select(baseAssignmentsSelectors.getCandidateReviews),
      this.store.select(baseAuthSelectors.getUserInfo),
    ])
      .pipe(
        takeUntil(this.destroy$),
        filter(([candidateReviewsState, userState]) => !!candidateReviewsState.ok && !!userState.ok),
        map(
          ([candidateReviewsState, userState]) =>
            [candidateReviewsState.data.ratings, userState.data] as [
              CustomerProfileRatingListItemResponse[],
              BaseTokenInterface
            ]
        )
      )
      .subscribe(([candidateReviews, loggedInUser]) => {
        this.ownReview = candidateReviews.find((review) => review.ratedBy!.id === loggedInUser.id)!;
        if (this.ownReview) {
          this.ownRatingFormGroup.controls.rating.setValue(this.ownReview.rating);
          this.ownRatingFormGroup.controls.note.setValue(this.ownReview?.note || '');
        }
        this.reviews = candidateReviews.filter((review) => review.ratedBy!.id !== loggedInUser.id);
      });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
  }

  getFormControl(control: AbstractControl) {
    return control as FormControl;
  }

  addOrEditReview(): void {
    if (this.isOwnReviewEdited) {
      this.submitted = true;
      if (this.ownRatingFormGroup.valid) {
        if (this.ownReview) {
          this.store.dispatch(
            baseAssignmentsActions.updateCandidateRating({
              payload: {
                rating: this.ownReview.id,
                body: this.ownRatingFormGroup.value,
                assignment: this.assignment,
                profileId: this.profileId,
              },
            })
          );
        } else {
          this.store.dispatch(
            baseAssignmentsActions.createCandidateRating({
              payload: {
                body: this.ownRatingFormGroup.value,
                assignment: this.assignment,
                profileId: this.profileId,
              },
            })
          );
        }
        this.ownRatingFormGroup.reset();
        this.submitted = false;
        this.isOwnReviewEdited = false;
      }
    } else {
      this.isOwnReviewEdited = true;
    }
  }

  cancelEditReview() {
    this.submitted = false;
    this.isOwnReviewEdited = false;
    if (this.ownReview) {
      this.ownRatingFormGroup.controls.rating.setValue(this.ownReview.rating);
      this.ownRatingFormGroup.controls.note.setValue(this.ownReview?.note || '');
    }
  }
}
